/*  ReviewQueueBell — admin header bell for the cross-queue review inbox.
    ─────────────────────────────────────────────────────────────────────
    Polls /api/admin/review-queue/summary every 60 s and surfaces:
      • Case validations awaiting a call
      • Course submissions awaiting approve/reject
      • Blog posts under review
    Native <details>/<summary> popover (matches EscalationsBell) with a
    linked row per queue and a "View all" footer.
*/
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    Tray, ClipboardText, GraduationCap, Newspaper, CheckCircle, ArrowRight,
} from "@phosphor-icons/react";


const POLL_INTERVAL_MS = 60_000;


function relativeAge(iso) {
    if (!iso) return null;
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
    if (mins < 60) return `${mins}m`;
    if (mins < 24 * 60) return `${Math.round(mins / 60)}h`;
    return `${Math.round(mins / 1440)}d`;
}


export default function ReviewQueueBell() {
    const [data, setData] = useState({
        case_validations: 0, course_reviews: 0, blog_reviews: 0, total: 0, oldest_at: null,
    });
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        api.get("/admin/review-queue/summary")
            .then((r) => setData(r.data))
            .catch(() => { /* silent — non-critical widget */ })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_INTERVAL_MS);
        return () => clearInterval(t);
    }, [load]);

    const total = data.total || 0;
    const oldest = relativeAge(data.oldest_at);
    // Turn amber when we have work; rose when the oldest item is > 24h old.
    const stale = data.oldest_at
        ? (Date.now() - new Date(data.oldest_at).getTime()) / 3_600_000 > 24
        : false;

    return (
        <details className="relative" data-testid="admin-review-queue-bell">
            <summary
                className="list-none cursor-pointer relative inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-secondary transition-colors select-none"
                title={total ? `${total} item${total === 1 ? "" : "s"} awaiting your review` : "Review queue is clear"}
                data-testid="admin-review-queue-toggle"
            >
                <Tray size={18} weight={total ? "fill" : "regular"} className={total ? "text-primary" : "text-muted-foreground"} />
                {total > 0 && (
                    <span
                        className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${stale ? "bg-rose-600" : "bg-amber-500"}`}
                        data-testid="admin-review-queue-count"
                    >
                        {total > 99 ? "99+" : total}
                    </span>
                )}
            </summary>

            <div className="absolute right-0 top-full mt-2 z-50 w-80 rounded-2xl border border-border bg-card shadow-2xl">
                <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Tray size={14} weight="fill" className="text-primary" />
                        <h3 className="font-display font-bold text-sm">
                            Awaiting your review {total > 0 && <span className="text-muted-foreground font-normal">· {total}</span>}
                        </h3>
                    </div>
                    {oldest && total > 0 && (
                        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${stale ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                            oldest {oldest}
                        </span>
                    )}
                </div>

                {loading ? (
                    <div className="px-4 py-6 text-center text-muted-foreground text-xs">Loading…</div>
                ) : total === 0 ? (
                    <div className="px-4 py-8 text-center" data-testid="admin-review-queue-empty">
                        <CheckCircle size={28} weight="duotone" className="mx-auto text-emerald-600 mb-2" />
                        <div className="text-sm font-semibold">Inbox zero</div>
                        <div className="text-xs text-muted-foreground mt-0.5">No cases, courses, or blog posts waiting for your decision.</div>
                    </div>
                ) : (
                    <ul className="divide-y divide-border">
                        <QueueRow
                            testid="review-queue-cases"
                            to="/admin/case-validations"
                            Icon={ClipboardText}
                            label="Case validations"
                            sublabel="Doctor case write-ups awaiting approve / reject"
                            count={data.case_validations}
                        />
                        <QueueRow
                            testid="review-queue-courses"
                            to="/admin/course-reviews"
                            Icon={GraduationCap}
                            label="Course submissions"
                            sublabel="New courses submitted by Certification Authors"
                            count={data.course_reviews}
                        />
                        <QueueRow
                            testid="review-queue-blogs"
                            to="/admin/blog?tab=review"
                            Icon={Newspaper}
                            label="Blog posts"
                            sublabel="Articles submitted for publishing"
                            count={data.blog_reviews}
                        />
                    </ul>
                )}
            </div>
        </details>
    );
}


function QueueRow({ testid, to, Icon, label, sublabel, count }) {
    return (
        <li data-testid={testid}>
            <Link
                to={to}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors no-underline text-foreground"
            >
                <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${count > 0 ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-secondary text-muted-foreground"}`}>
                    <Icon size={16} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{label}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{sublabel}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <span className={`min-w-[24px] h-6 px-1.5 rounded-full text-[11px] font-bold grid place-items-center ${count > 0 ? "bg-amber-500 text-white" : "bg-secondary text-muted-foreground"}`}>
                        {count}
                    </span>
                    <ArrowRight size={12} weight="bold" className="text-muted-foreground" />
                </div>
            </Link>
        </li>
    );
}
